/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Actions,
  Async,
  AsyncValue,
  toAsyncFetched,
  toOptionsConstructor,
} from '@client/new/helpers/types';
import { z } from 'zod';
import {
  DiscountType,
  salesTypeValues,
  subscriptionTypeValues,
  discountTypeValues,
  SalesType,
  SubscriptionType,
} from '../../collection/domain';
import {
  timezoneValues,
  dayTimeValues,
} from '../../workout/components/Schedule';
import { createExpirationDate, formatExpiry } from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { DiscountsModel } from '../model/Discounts.model';
import {
  calculatePrices,
  mapNewDiscountToCreateCouponVar,
  PricesVM,
  randomCode,
} from './newDiscount.mappers';
import { toErrors } from '@client/new/helpers/forms';
import toast from 'react-hot-toast';
import { DiscountsVM, toQueryParamsSchema } from './discounts.viewModel';
import { useEffect, useState } from 'react';
import { validateNewDiscountChange } from './newDiscount.validation';
import { otpSelectionMapper, transformValue } from './newDiscount.transformers';
import { keys } from '@client/new/helpers/general';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { BundleStatus } from 'generated/graphql';

const productType = toOptionsConstructor<SalesType>();
const productTypes = [
  productType('subscription', 'Subscription'),
  productType('otp', 'One-Time-Purchases'),
];

const subType = toOptionsConstructor<SubscriptionType>();
const subTypes = [subType('monthly', 'Monthly'), subType('annual', 'Annual')];

const discountType = toOptionsConstructor<DiscountType>();
export const discountTypes = [
  discountType('Percentage', 'Percentage discount'),
  discountType('FixedAmount', 'Fixed amount discount'),
];

const otpTypeValues = ['program', 'bundle'] as const;

export const newDiscountFormSchema = z.object({
  name: z.string(),
  discountCode: z.string(),
  salesType: z.enum(salesTypeValues).optional(),
  subscriptionType: z
    .union([z.enum(subscriptionTypeValues), z.literal('')])
    .optional()
    .nullable(),
  otpId: z.string().optional(),
  otpType: z.enum(otpTypeValues).optional(),

  discountType: z.enum(discountTypeValues).optional().nullable(),
  discountFixedValue: z.number().optional(),
  discountPercentValue: z.number().optional(),

  hasExpiration: z.boolean(),
  expiration: z.preprocess(
    (val) => (val === '' ? null : val),
    z.number().nullable().optional(),
  ),
  expirationTimezone: z.enum(timezoneValues).optional().nullable(),
  expirationHour: z.number().min(0).max(11).optional(),
  expirationMinute: z.number().min(0).max(59).optional(),
  expirationDayTime: z
    .union([z.enum(dayTimeValues), z.literal('')])
    .optional()
    .nullable(),
  hasRedemptionsLimit: z.boolean(),
  redemptionsLimit: z.number().optional(),
  compatibleWithFreeTrial: z.boolean().default(false),
});

export type NewDiscount = z.infer<typeof newDiscountFormSchema>;
const emptyNewDiscount = (
  delta: Partial<NewDiscount>,
): Partial<NewDiscount> => ({
  name: '',
  discountCode: '',
  discountType: null,
  hasExpiration: false,
  expirationTimezone: 'America/New_York',
  hasRedemptionsLimit: false,
  salesType: 'subscription',
  expirationHour: 0,
  discountFixedValue: 0.01,
  discountPercentValue: 0.01,
  expirationMinute: 0,
  expirationDayTime: 'AM',
  redemptionsLimit: 100,
  ...delta,
});

type NewDiscountActionMap = { GenerateCode: null };
type NewDiscountAction = Actions<NewDiscountActionMap>;

export const useNewDiscountVM = (
  model: Async<DiscountsModel>,
  discountVMDispatch: DiscountsVM['dispatch'],
) => {
  const [inProgress, setInProgress] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [prices, setPrices] = useState<Async<PricesVM>>({
    type: 'not-fetched',
  });

  const p = toQueryParamsSchema();
  const delta: Partial<NewDiscount> =
    p.mode === 'create' && p.programId
      ? { otpId: p.programId, salesType: 'otp', otpType: 'program' }
      : p.mode === 'create' && p.offerId
        ? { otpId: p.offerId, salesType: 'otp', otpType: 'bundle' }
        : {};

  useEffect(() => {
    if (p.mode === 'create') {
      discountVMDispatch('ToggleModal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { handleSubmit, formState, setValue, ...form } = useForm<NewDiscount>({
    defaultValues: emptyNewDiscount(delta),
    reValidateMode: 'onChange',
    resolver: zodResolver(
      newDiscountFormSchema.superRefine(
        validateNewDiscountChange(() => {
          try {
            return model.type !== 'fetched'
              ? []
              : model.value.discounts
                  .filter((d) => d.status === 'Active')
                  .map((d) => d.code);
          } catch (e) {
            return [];
          }
        }),
      ),
    ),
  });

  const values = form.watch();

  useEffect(() => {
    if (model.type !== 'fetched') return;
    if (!values.otpId) return;
    if (values.otpId && values.otpType === 'bundle') {
      const bundlePrice =
        model.value.otpBundles.find((b) => b.id === Number(values.otpId))
          ?.oneTimeProduct?.price || 0;
      model.value.setOtpPrice(values.otpId, bundlePrice);
      return;
    }
    model.value.fetchOTPPrice(parseInt(values.otpId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.otpId, model.type]);

  useEffect(() => {
    if (model.type !== 'fetched') return;
    const nextPrices = calculatePrices(model.value, values);
    if (JSON.stringify(nextPrices) === JSON.stringify(prices)) return;
    setPrices(nextPrices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, values]);

  const register = <T extends keyof NewDiscount>(key: T) => {
    const { onChange: orgOnChange, ...orgRegister } = form.register(key);
    const onChange = (e: React.ChangeEvent<any>) => {
      if (inProgress || model.type !== 'fetched') return;

      const delta =
        key === 'otpId'
          ? otpSelectionMapper(e.target.value)
          : transformValue({
              value: e.target.value,
              model: model.value,
              values,
              prices,
              key,
            });

      keys(delta).forEach((k) =>
        k === key
          ? setValue(k, delta[k], { shouldValidate: true })
          : setValueOf({ name: k, value: delta[k] }),
      );

      if (Object.prototype.hasOwnProperty.call(delta, key))
        //@ts-ignore
        e.target.value = delta[key];
      return orgOnChange(e);
    };

    return {
      ...orgRegister,
      disabled: inProgress || orgRegister.disabled,
      onChange,
      name: key,
      value: values[key],
      error: formState.errors[key]?.message,
    };
  };

  const setValueOf = <T extends keyof NewDiscount>(target: {
    name: T;
    value: NewDiscount[T];
  }) => register(target.name).onChange({ target } as any);

  function onSubmit(data: NewDiscount) {
    if (model.type !== 'fetched') return;
    setInProgress(true);
    const payload = mapNewDiscountToCreateCouponVar(
      data,
      model.value.subscriptionMap,
      0,
    );
    const bundle = data.otpId
      ? model.value.otpBundles.find((b) => b.id === Number(data.otpId))
      : null;
    const hideModalForDraftBundle =
      !!bundle && bundle.status !== BundleStatus.Published;
    const isCreatorLive = model.value.isCreatorLive;

    trackSessionEvent('ViewAction', { type: 'CreateDiscount', value: payload });
    model.value
      .createDiscount(payload)
      .then((d) => {
        discountVMDispatch('ToggleModal');
        !hideModalForDraftBundle &&
          isCreatorLive &&
          discountVMDispatch('OpenConfirmModal', d.url || '');
        form.reset();
        toast.success('Discount created');
      })
      .catch((e) => {
        console.error('error creating', e);
        toast.error('Error creating discount ' + (e?.message || ''));
      })
      .finally(() => {
        setInProgress(false);
        setShowErrors(false);
      });
  }

  if (model.type !== 'fetched') return model;

  const availableSubTypes = subTypes.filter((opt) =>
    Boolean(model.value.subscriptionMap[opt.value]),
  );

  const availableProductTypes = productTypes.map((opt) => ({
    ...opt,
    disabled:
      opt.value === 'subscription'
        ? !model.value.hasSubscriptionProducts
        : !model.value.hasOtpProducts,
  }));

  const dispatch = (v: NewDiscountAction) => {
    trackSessionEvent('ViewAction', v);
    switch (v.type) {
      case 'GenerateCode':
        setValueOf({ value: randomCode(8), name: 'discountCode' });
        break;
    }
  };
  const expirationDesc = values.expiration
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatExpiry(createExpirationDate(values as any))
    : '';
  return toAsyncFetched({
    register,
    onSubmit: () => {
      if (inProgress) return;
      setShowErrors(true);
      handleSubmit(onSubmit)();
    },
    expirationDesc,
    values,
    dispatch,
    prices,
    subTypes: availableSubTypes,
    productTypes: availableProductTypes,
    inProgress,
    isCouponWithFreeTrialEnabled: model.value.isCouponWithFreeTrialEnabled,
    errors: showErrors ? toErrors(formState.errors) : null,
  });
};

export type NewDiscountVM = AsyncValue<ReturnType<typeof useNewDiscountVM>>;
